import { Dispatch, useEffect } from 'react'

import { isEqual } from 'lodash'

import { deriveFiltersFromDateFilter } from 'modules/ai_reports/components/DatasourceTypeRenderer'
import { datatableFiltersToJsonLogicFilters } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { ReportSchemaType } from 'modules/ai_reports/lib/types'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'

import { ReportAction, ReportState } from './useReportCoreState'

type UseReportFiltersSyncOptions = {
  state: ReportState
  schema: ReportSchemaType
  dispatch: Dispatch<ReportAction>
  agentState: AiReportsResult | null
  setAgentState: (state: AiReportsResult) => void
  datasourceId: string
}

/**
 * Handles bidirectional synchronization between report filters and agent state.
 * - Syncs agent state changes to local state
 * - Syncs date filter changes to agent
 * - Syncs datatable filter changes to agent
 */
export function useReportFiltersSync({
  state,
  schema,
  dispatch,
  agentState,
  setAgentState,
  datasourceId,
}: UseReportFiltersSyncOptions) {
  // Sync agent state changes to local state
  useEffect(() => {
    if (agentState) {
      dispatch({ type: 'AGENT_STATE_CHANGED', payload: agentState })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agentState?.sqlAnswer?.sql,
    agentState?.sqlAnswer?.description,
    agentState?.sqlAnswer?.column_format_hints,
    agentState?.filters?.jsonLogicFilters,
    agentState?.filters?.dateRange?.from,
    agentState?.filters?.dateRange?.to,
    agentState?.filters?.snapshotDate,
  ])

  // Sync date filter changes to agent state.
  // Guarded by value-equality on the derived shape so we don't cycle back
  // into the reducer with fresh object references.
  useEffect(() => {
    if (!agentState) {
      return
    }

    const derivedDateFilters = deriveFiltersFromDateFilter(state.dateFilter, datasourceId)
    if (!derivedDateFilters) {
      return
    }

    if ('snapshotDate' in derivedDateFilters) {
      if (derivedDateFilters.snapshotDate === agentState.filters?.snapshotDate) return
    } else if ('dateRange' in derivedDateFilters) {
      const sameFrom = derivedDateFilters.dateRange?.from === agentState.filters?.dateRange?.from
      const sameTo = derivedDateFilters.dateRange?.to === agentState.filters?.dateRange?.to
      if (sameFrom && sameTo) return
    } else {
      // Nothing derived — nothing to sync.
      return
    }

    setAgentState({
      ...agentState,
      filters: {
        ...agentState.filters,
        ...derivedDateFilters,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dateFilter, datasourceId])

  // Sync datatable filters changes to agent state.
  // Guarded: skip setAgentState when the structural form of the filter
  // matches what's already stored. Comparing via `isEqual` on the parsed
  // object avoids false negatives from non-canonical JSON key order — a
  // pure `JSON.stringify ===` compare would flap when either side
  // serialised keys in a different order and re-open the render loop.
  useEffect(() => {
    if (!agentState || schema.length === 0) {
      return
    }

    const jsonLogicFilters = datatableFiltersToJsonLogicFilters(
      state.datatableFilters || {},
      schema
    )
    const currentParsed = agentState.filters?.jsonLogicFilters
      ? safeParse(agentState.filters.jsonLogicFilters)
      : null
    // Treat undefined and null as equivalent (both mean "no filters")
    if (!jsonLogicFilters && !currentParsed) return
    if (isEqual(jsonLogicFilters, currentParsed)) return

    setAgentState({
      ...agentState,
      filters: {
        ...agentState.filters,
        jsonLogicFilters: JSON.stringify(jsonLogicFilters),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.datatableFilters, schema])
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}
